<template>
    <div class="profile-details w-100">
        <b-row>
            <b-col lg="4" class="content">
                <table class="table details table-borderless min-th">
                    <tbody>
                        <tr>
                            <th>Date:</th>
                            <td>
                                <date-picker
                                    v-if="isactiveClient && !theReturn.status"
                                    id="date"
                                    name="date"
                                    :config="datePickerOptions"
                                    v-model="theDate"
                                    placeholder="Date (required)"
                                    v-validate="'required'"
                                    :class="{
                                        'border-danger': errors.has('date')
                                    }"
                                />
                                <span v-else>
                                    {{
                                        _carbonDateDisplay(
                                            theReturn.return_date
                                        )
                                    }}
                                </span>
                            </td>
                        </tr>
                        <tr>
                            <th>Client:</th>
                            <td>
                                <span v-if="itemCount > 0">{{
                                    theReturn.client
                                        ? theReturn.client.label
                                        : ""
                                }}</span>
                                <vet-select
                                    v-else
                                    id="client"
                                    ref="select-client"
                                    v-model="theClient"
                                    :show-menu="false"
                                    name="client"
                                    module="client"
                                    v-validate="'required'"
                                    placeholder="Client name (required)"
                                    :class="{
                                        'border-danger': errors.has('client')
                                    }"
                                />
                            </td>
                        </tr>
                        <tr>
                            <th>Amount:</th>
                            <td>
                                <span>
                                    {{
                                        _toCurrency(
                                            theReturn.return_amount || 0
                                        )
                                    }}
                                </span>
                            </td>
                        </tr>
                        <tr>
                            <th>Notes:</th>
                            <td>
                                <b-form-textarea
                                    v-model="theNotes"
                                    placeholder="Notes (optional)"
                                    rows="3"
                                    name="note"
                                ></b-form-textarea>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </b-col>
            <b-col lg="4" class="content">
                <table class="table details table-borderless">
                    <tbody>
                        <tr>
                            <th>Credit Issued:</th>
                            <td>
                                <div v-if="fullDetails.credit">
                                    {{ _toCurrency(fullDetails.credit.amount) }}
                                    (<span class="text-nowrap">{{
                                        fullDetails.credit.name
                                    }}</span
                                    >)
                                </div>
                                <span v-else>{{ _toCurrency(0) }}</span>
                            </td>
                        </tr>
                        <tr>
                            <th>Refund Issued:</th>
                            <td>
                                <div v-if="fullDetails.refunds.length > 0">
                                    {{
                                        _toCurrency(
                                            getRefundAmount(fullDetails.refunds)
                                        )
                                    }}
                                    (<span
                                        v-for="(refund,
                                        indexRefund) in fullDetails.refunds"
                                        :key="refund.id"
                                    >
                                        <span class="text-nowrap">{{
                                            refund.name
                                        }}</span>
                                        <span
                                            v-if="
                                                indexRefund !=
                                                fullDetails.refunds.length - 1
                                            "
                                            >,
                                        </span> </span
                                    >)
                                </div>
                                <span v-else>{{ _toCurrency(0) }}</span>
                            </td>
                        </tr>
                        <tr v-if="theReturn.status == 1">
                            <th>Post Date:</th>
                            <td>
                                {{ _carbonDateDisplay(theReturn.post_date) }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </b-col>
        </b-row>
    </div>
</template>
<style type="text/css" scoped>
.table.details tbody tr th {
    width: 150px;
}
</style>
<script>
/**
 * @name ReturnDetailsEdit
 * @description Displaying edit details of return
 * @see apps/application/views/staff/billing/return/return/create_update.php
 */
import { forEach } from "lodash";
import VetSelect from "@/module/commons/vet-select.vue";
import RELATIONS from "@/constants/relations.js";
import BillingReturn from "@/services/billing/billing-return.js";

export default {
    components: { VetSelect },
    data() {
        return {
            isRendered: false,
            activeTabIndex: 0,
            datePickerOptions: {
                format: ""
            },
            theDate: null,
            theClient: null,
            theNotes: null
        };
    },
    computed: {
        isLoading() {
            return this.$store.getters["returnDetails/isLoading"];
        },
        theReturn() {
            return this.$store.getters["returnDetails/returnDetails"];
        },
        returnEdit() {
            let theReturn = this.$store.getters["returnDetails/returnDetails"];
            return theReturn ? _.clone(theReturn) : null;
        },
        fullDetails() {
            return this.$store.getters["returnDetails/fullDetails"];
        },
        itemCount() {
            return this.$store.getters["returnDetails/itemCount"];
        },
        clientDetails() {
            let theReturn = this.$store.getters["returnDetails/returnDetails"];
            let client =
                theReturn && theReturn.client ? theReturn.client : null;

            if (client) {
                client.isactive = client.single_business_actor
                    ? client.single_business_actor.isactive
                    : false;
            }

            return client;
        },
        isactiveClient() {
            let client = this.theReturn.client
                ? this.theReturn.client.single_business_actor
                : null;

            if (client) {
                return (
                    client.isactive &&
                    client.relation_type == RELATIONS.BUSINESS_ACTOR.CLIENT
                );
            }

            return false;
        }
    },
    mounted() {
        this.initValues();
    },
    methods: {
        async initValues() {
            this.datePickerOptions.format = this.getDateFormat();

            this.$nextTick(() => {
                this.theDate = this._now().toFormat(this._dateFormat);

                if (this.returnEdit) {
                    this.theDate = this._carbonDateDisplay(
                        this.returnEdit.return_date
                    );
                    this.theClient = this.returnEdit.client;
                    this.theNotes = this.returnEdit.note;
                }
            });

            if (this.itemCount <= 0) {
                this.$refs["select-client"].focus();
            }
        },
        getRefundAmount(refunds) {
            let total = 0;
            forEach(refunds, function (value) {
                total += value.amount;
            });

            return total;
        },
        async save() {
            let valid = await this.$validator.validateAll();
            if (!valid) {
                return false;
            }

            this.$store.commit("returnDetails/setLoading", true);

            let params = {
                client_id: this.theClient.id,
                return_date: this._fromDateStrToDateTime(this.theDate, true),
                note: this.theNotes
            };

            BillingReturn.updateReturn(this.theReturn.id, params)
                .then(response => {
                    if (response.code == 200) {
                        this.$store.commit("returnDetails/setEditMode", false);
                        this.$root.$emit("refresh-return-page");
                        this._message("Changes saved successfully");
                    } else if (response.code == 400) {
                        this._message(response.message, "danger");
                    }
                })
                .catch(error => {
                    this._message("Error : Unable to save changes", "danger");
                    console.error(error);
                })
                .finally(() => {
                    this.$store.commit("returnDetails/setLoading", false);
                });
        }
    }
};
</script>
